import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import * as d3 from 'd3';

const Footer = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  const currentYear = new Date().getFullYear();
  const svgRef = useRef();
  const animationRef = useRef();

  const quickLinks = [
    { name: 'About', href: '#about' },
    { name: 'Products', href: '#products' },
    { name: 'Quality', href: '#quality' },
    { name: 'Contact', href: '#contact' }
  ];

  const handleLinkClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // D3.js animated background for footer
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const container = svgRef.current.parentElement;
    const width = container.offsetWidth || 1200;
    const height = 300;

    svg.attr('width', width).attr('height', height);

    // Create gradient definitions
    const defs = svg.append('defs');

    // Steel pipe gradient
    const pipeGradient = defs.append('linearGradient')
      .attr('id', 'footerPipeGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');

    if (isDarkMode) {
      pipeGradient.selectAll('stop').data([
        { offset: '0%', color: 'rgba(247, 148, 29, 0.8)' },
        { offset: '20%', color: 'rgba(200, 200, 200, 0.6)' },
        { offset: '40%', color: 'rgba(120, 120, 120, 0.4)' },
        { offset: '60%', color: 'rgba(200, 200, 200, 0.6)' },
        { offset: '100%', color: 'rgba(247, 148, 29, 0.8)' }
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color);
    } else {
      pipeGradient.selectAll('stop').data([
        { offset: '0%', color: 'rgba(247, 148, 29, 0.9)' },
        { offset: '20%', color: 'rgba(180, 180, 180, 0.7)' },
        { offset: '40%', color: 'rgba(100, 100, 100, 0.5)' },
        { offset: '60%', color: 'rgba(180, 180, 180, 0.7)' },
        { offset: '100%', color: 'rgba(247, 148, 29, 0.9)' }
      ]).enter().append('stop')
        .attr('offset', d => d.offset)
        .attr('stop-color', d => d.color);
    }

    // Background mesh pattern
    const pattern = defs.append('pattern')
      .attr('id', 'footerMesh')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 40)
      .attr('height', 40);

    pattern.append('rect')
      .attr('width', 40)
      .attr('height', 40)
      .attr('fill', 'none');

    pattern.selectAll('line').data([
      { x1: 0, y1: 0, x2: 40, y2: 0 },
      { x1: 0, y1: 20, x2: 40, y2: 20 },
      { x1: 0, y1: 40, x2: 40, y2: 40 },
      { x1: 0, y1: 0, x2: 0, y2: 40 },
      { x1: 20, y1: 0, x2: 20, y2: 40 },
      { x1: 40, y1: 0, x2: 40, y2: 40 }
    ]).enter().append('line')
      .attr('x1', d => d.x1).attr('y1', d => d.y1)
      .attr('x2', d => d.x2).attr('y2', d => d.y2)
      .attr('stroke', isDarkMode ? 'rgba(247, 148, 29, 0.1)' : 'rgba(247, 148, 29, 0.15)')
      .attr('stroke-width', 0.5);

    // Background with pattern
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'url(#footerMesh)')
      .attr('opacity', 0.3);

    // Create floating steel particles
    const particles = [];
    const numParticles = Math.floor(width / 60);

    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 2 + 1,
        opacity: Math.random() * 0.6 + 0.2,
        life: Math.random() * 100
      });
    }

    const particleGroup = svg.append('g').attr('class', 'footer-particles');

    const particleElements = particleGroup.selectAll('circle')
      .data(particles)
      .enter()
      .append('circle')
      .attr('fill', 'url(#footerPipeGradient)')
      .attr('r', d => d.radius);

    // Create animated steel pipes (horizontal)
    const pipeData = [
      { y: 50, thickness: 8, speed: 1 },
      { y: 150, thickness: 6, speed: 1.5 },
      { y: 250, thickness: 10, speed: 0.8 }
    ];

    const pipeGroup = svg.append('g').attr('class', 'footer-pipes');

    pipeData.forEach((pipe, index) => {
      // Main pipe
      pipeGroup.append('rect')
        .attr('class', `footer-pipe-${index}`)
        .attr('x', 0)
        .attr('y', pipe.y - pipe.thickness/2)
        .attr('width', width)
        .attr('height', pipe.thickness)
        .attr('fill', 'url(#footerPipeGradient)')
        .attr('opacity', 0.6);

      // Pipe highlights
      pipeGroup.append('rect')
        .attr('class', `footer-pipe-highlight-${index}`)
        .attr('x', 0)
        .attr('y', pipe.y - pipe.thickness/2)
        .attr('width', width)
        .attr('height', 1)
        .attr('fill', 'rgba(255, 255, 255, 0.3)');

      // Pipe shadows
      pipeGroup.append('rect')
        .attr('class', `footer-pipe-shadow-${index}`)
        .attr('x', 0)
        .attr('y', pipe.y + pipe.thickness/2 - 1)
        .attr('width', width)
        .attr('height', 1)
        .attr('fill', 'rgba(0, 0, 0, 0.3)');
    });

    // Animation loop
    let time = 0;
    const animate = () => {
      time += 0.01;

      // Update particles
      particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life += 1;

        // Wrap around edges
        if (particle.x < 0) particle.x = width;
        if (particle.x > width) particle.x = 0;
        if (particle.y < 0) particle.y = height;
        if (particle.y > height) particle.y = 0;

        // Fade and regenerate
        if (particle.life > 200) {
          particle.life = 0;
          particle.opacity = Math.random() * 0.6 + 0.2;
          particle.x = Math.random() * width;
          particle.y = Math.random() * height;
        }

        const fadeOpacity = particle.life < 50 ? particle.life / 50 :
                           particle.life > 150 ? (200 - particle.life) / 50 : 1;
        particle.currentOpacity = particle.opacity * fadeOpacity * 0.7;
      });

      particleElements
        .attr('cx', d => d.x)
        .attr('cy', d => d.y)
        .attr('opacity', d => d.currentOpacity);

      // Animate pipe highlights (flowing effect)
      pipeData.forEach((pipe, index) => {
        const flowOffset = (time * pipe.speed * 100) % (width + 200);

        pipeGroup.select(`.footer-pipe-highlight-${index}`)
          .attr('x', flowOffset - 200)
          .attr('width', 200)
          .attr('opacity', 0.4 + Math.sin(time * pipe.speed * 2) * 0.2);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      const newWidth = container.offsetWidth || 1200;
      svg.attr('width', newWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode]);

  return (
    <footer className={`${colors.bg.card} relative transition-colors duration-300 overflow-hidden`}>
      {/* Animated D3.js Background */}
      <div className="absolute inset-0" style={{ zIndex: 0 }}>
        <svg
          ref={svgRef}
          className="w-full h-full"
          style={{ mixBlendMode: isDarkMode ? 'screen' : 'multiply' }}
        />
      </div>

      {/* Gradient overlay for better text readability */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-b from-gray-900/80 via-gray-900/60 to-gray-900/80'
        : 'bg-gradient-to-b from-white/80 via-white/60 to-white/80'
      }`} style={{ zIndex: 1 }} />

      {/* Subtle top border with glow effect */}
      <div className="relative" style={{ zIndex: 2 }}>
        <div className={`w-full h-px ${colors.border.muted} relative`}>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-400/50 to-transparent" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" style={{ zIndex: 2 }}>
        {/* Main footer content */}
        <div className="py-16">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {/* Company Info */}
            <motion.div
              className="lg:col-span-2"
              variants={fadeInUp}
            >
              <div className="mb-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="group"
                >
                  <div className="relative mb-6">
                    <motion.img
                      src="/Julfikarlogo.png"
                      alt="Julfikar Steel"
                      className="h-16 w-auto filter drop-shadow-lg transition-all duration-300 group-hover:scale-105"
                      whileHover={{
                        scale: 1.05,
                        filter: "drop-shadow(0 0 20px rgba(247, 148, 29, 0.5))"
                      }}
                    />
                    <div className="absolute -bottom-2 left-0 w-20 h-1 bg-gradient-to-r from-primary-400/80 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                  </div>
                </motion.div>
                <motion.p
                  className={`${colors.text.secondary} text-base leading-relaxed max-w-md backdrop-blur-sm p-4 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} border border-primary-400/20`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  Modern steel re-rolling mill delivering precision steel products
                  with reliable supply and consistent quality. Excellence in every steel product.
                </motion.p>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                {[
                  {
                    icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z",
                    text: "Industrial Area, Steel City, SC 12345",
                    delay: 0.3
                  },
                  {
                    icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
                    text: "sales@julfikarsteel.com",
                    delay: 0.4
                  },
                  {
                    icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
                    text: "+1 (555) 123-4567",
                    delay: 0.5
                  }
                ].map((contact, index) => (
                  <motion.div
                    key={index}
                    className={`flex items-center space-x-4 ${colors.text.secondary} text-sm p-3 rounded-lg backdrop-blur-sm ${isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'} border border-transparent hover:border-primary-400/30 transition-all duration-300 group`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: contact.delay }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <motion.div
                      className="w-8 h-8 rounded-full bg-primary-400/20 flex items-center justify-center group-hover:bg-primary-400/30 transition-colors duration-300"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <svg className="w-4 h-4 text-primary-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={contact.icon} />
                      </svg>
                    </motion.div>
                    <span className="group-hover:text-primary-300 transition-colors duration-300">{contact.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div variants={fadeInUp}>
              <motion.h4
                className={`${colors.text.primary} font-semibold mb-6 text-sm uppercase tracking-wider relative`}
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                Quick Links
                <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-primary-400 to-transparent" />
              </motion.h4>
              <ul className="space-y-4">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 + index * 0.1 }}
                  >
                    <motion.a
                      href={link.href}
                      className={`${colors.text.secondary} hover:text-primary-400 transition-all duration-300 text-sm flex items-center space-x-2 p-2 rounded-md hover:bg-primary-400/10 group relative`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLinkClick(link.href);
                      }}
                      whileHover={{ x: 5 }}
                    >
                      <div className="w-1 h-1 rounded-full bg-primary-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <span className="group-hover:font-medium transition-all duration-300">{link.name}</span>
                      <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-x-2 group-hover:translate-x-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </motion.a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Business Hours & CTA */}
            <motion.div variants={fadeInUp}>
              <motion.h4
                className={`${colors.text.primary} font-semibold mb-6 text-sm uppercase tracking-wider relative`}
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.1 }}
              >
                Business Hours
                <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-primary-400 to-transparent" />
              </motion.h4>

              <motion.div
                className="space-y-3 mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 }}
              >
                {[
                  { day: 'Mon - Fri', time: '8:00 AM - 6:00 PM', status: 'open' },
                  { day: 'Saturday', time: '9:00 AM - 4:00 PM', status: 'open' },
                  { day: 'Sunday', time: 'Closed', status: 'closed' }
                ].map((schedule, index) => (
                  <motion.div
                    key={index}
                    className={`flex justify-between items-center text-sm p-2 rounded-md ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} border border-primary-400/10`}
                    initial={{ opacity: 0, x: 15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 1.3 + index * 0.1 }}
                  >
                    <span className={`${colors.text.secondary} font-medium`}>{schedule.day}</span>
                    <span className={`${schedule.status === 'open' ? 'text-green-400' : 'text-red-400'} font-medium`}>
                      {schedule.time}
                    </span>
                  </motion.div>
                ))}
              </motion.div>

              {/* Enhanced CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.6 }}
              >
                <motion.a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick('#contact');
                  }}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-400 to-primary-500 text-black text-sm font-bold rounded-xl hover:from-primary-300 hover:to-primary-400 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 group relative overflow-hidden"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <svg className="w-5 h-5 mr-2 group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="relative z-10">Get Your Quote</span>
                  <motion.svg
                    className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ x: [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </motion.svg>
                </motion.a>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Enhanced Bottom Bar */}
        <motion.div
          className={`border-t ${colors.border.muted} py-8 relative`}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.8 }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-6 sm:space-y-0">
            {/* Enhanced Copyright */}
            <motion.div
              className={`${colors.text.muted} text-sm text-center sm:text-left`}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 1.9 }}
            >
              <div className="flex items-center space-x-2 justify-center sm:justify-start">
                <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>© {currentYear} <span className="text-primary-400 font-semibold">Julfikar Steel</span>. All rights reserved.</span>
              </div>
              <div className="mt-1 text-xs opacity-75">
                Crafted with precision • Built for excellence
              </div>
            </motion.div>

            {/* Enhanced Social Links */}
            <motion.div
              className="flex items-center space-x-3"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 2.0 }}
            >
              <span className={`text-xs ${colors.text.muted} mr-2 hidden sm:block`}>Follow us:</span>
              {[
                {
                  name: 'LinkedIn',
                  icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
                  color: '#0077B5',
                  delay: 2.1
                },
                {
                  name: 'Twitter',
                  icon: 'M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z',
                  color: '#1DA1F2',
                  delay: 2.2
                },
                {
                  name: 'Instagram',
                  icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
                  color: '#E4405F',
                  delay: 2.3
                }
              ].map((social) => (
                <motion.a
                  key={social.name}
                  href="#"
                  className={`w-10 h-10 rounded-xl ${isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-black/10 hover:bg-black/20'} flex items-center justify-center ${colors.text.muted} hover:text-white transition-all duration-300 group relative overflow-hidden border border-transparent hover:border-primary-400/30`}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: social.delay }}
                  whileHover={{
                    scale: 1.15,
                    backgroundColor: social.color,
                    rotateY: 360
                  }}
                  whileTap={{ scale: 0.9 }}
                >
                  <motion.div
                    className="absolute inset-0 rounded-xl"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 0.1 }}
                    style={{ backgroundColor: social.color }}
                  />
                  <motion.svg
                    className="w-5 h-5 relative z-10"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    whileHover={{ scale: 1.1 }}
                  >
                    <path d={social.icon} />
                  </motion.svg>
                  <span className="sr-only">{social.name}</span>

                  {/* Hover glow effect */}
                  <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                       style={{
                         background: `radial-gradient(circle, ${social.color}40 0%, transparent 70%)`
                       }} />
                </motion.a>
              ))}
            </motion.div>
          </div>

          {/* Animated divider line */}
          <motion.div
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-400 to-transparent"
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: 96, opacity: 1 }}
            transition={{ duration: 1, delay: 2.4 }}
          />
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;